import React, { useMemo } from 'react'
import styled from 'styled-components'
import TimeAgo from 'react-timeago'
import { BASE, BLAST } from '../../../../constants'
import { Memecoin, TransactionEvent } from '../../../../types/reducerTypes'
import { Box } from '../../../../shared_components/UI/Box'
import { obfuscateEthWalletAddress } from '../../../../shared_components/SocialConnectButtons/Twitter'
import InfluencerCell from '../../../../shared_components/Library/Influencers/InfluencerCell'
import ChevronRight from '../../../../shared_components/Icons/ChevronRight'
import {
  formatNumberToFirstDecimalDigit,
  formatNumberWithDecimalSubscript,
  getTransactionEthAmount,
  getTransactionTokenAmount,
  getZviIconIndex,
  zviIcons,
} from '../helpers'
import { showDecimalsIfNeeded } from '../../../Dashboard/Allocations/InvestmentInfo'
import { H4, P, Strong } from '../../../../shared_components/Library/Typeography'
import { useSelector } from 'react-redux'
import { selectEthUsdPrice } from '../../../../modules/web3/selectors'
import { getLatestMemecoinPriceInEth } from '../../../../utils'

function PureTradesTable({
  memecoin,
  transactions,
  pagination,
}: {
  memecoin: Memecoin
  transactions: TransactionEvent[]
  pagination?: React.ReactNode
}) {

  const latestTransaction = transactions[0]


  const ethPriceInUsd = useSelector(selectEthUsdPrice)

  const tokenPrice = useMemo(() => {
    if (!memecoin) return 0

    const tokenPriceInEth = getLatestMemecoinPriceInEth(
      latestTransaction,
      memecoin.decimals ?? 6,
    )

    if (!tokenPriceInEth) return 0

    return tokenPriceInEth * ethPriceInUsd


  }, [memecoin, ethPriceInUsd, getLatestMemecoinPriceInEth, transactions])

  if (transactions.length === 0)
    return (
      <H4 tac style={{ width: '100%' }}>
        No trades yet
      </H4>
    )

  const handleClickRow = (txHash: string) => {
    // open link in new tab
    const url = `${(memecoin.chainId === String(BASE.chainId) ? BASE : BLAST)
        .explorerUrl
      }/tx/${txHash}`

    window.open(url, '_blank')
  }

  return (
    <Box dc g={24}>
      <Box style={{ overflow: 'scroll' }}>
        <Table>
          <thead>
            <TableRow>
              <th>
                <Box g={4}>
                  <P s="M" c="text-secondary">Account</P>
                </Box>
              </th>
              <th>
                <Box g={4} jfe ac>
                  <P s="M" c="text-secondary">Txn</P>
                </Box>
              </th>
              <th>
                <P s="M" c="text-secondary">Amount</P>
              </th>
            </TableRow>
          </thead>
          <tbody>
            {transactions.map(txEvent => {
              return (
                <TableBodyRow
                  key={txEvent._id}
                  onClick={() => handleClickRow(txEvent.transactionHash)}
                >
                  <TableCell>
                    <Box jfs ac g={12}>
                      <div style={{
                        gap: '4px',
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                        <img
                          src={zviIcons[getZviIconIndex(txEvent.nansenScore)]}
                          alt="zvi"
                          height="32px"
                          width="32px"
                        />
                        <P s="M">{txEvent.nansenScore}</P>
                      </div>
                      <div style={{
                        gap: '4px',
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                        <Strong s="L">{obfuscateEthWalletAddress(txEvent.walletAddress)}</Strong>
                      </div>
                      {/* <Box g={4} ac jfs alignSelf='flex-start'>
                        <img
                          src={zviIcons[getZviIconIndex(txEvent.nansenScore)]}
                          alt="zvi"
                          height="32px"
                          width="32px"
                        />
                        <P s="M">{txEvent.nansenScore}</P>
                      </Box>
                      <Box jfs ac alignSelf='flex-start'>
                        <Strong s="L">{obfuscateEthWalletAddress(txEvent.walletAddress, false)}</Strong>
                      </Box> */}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box dc ac jfe>
                      <Box g={2} afe alignSelf='flex-end' jfe>
                        <img
                          src={`/img/icons/memecoins/table/${txEvent?.event.toLowerCase()}.svg`} alt={txEvent?.event}
                          height="16px"
                          width="16px"
                        />
                        <Strong c={txEvent.event === 'Purchase' ? 'function-success' : 'function-error'}>
                          {txEvent.event === 'Purchase' ? 'Buy' : 'Sell'}
                        </Strong>
                      </Box>
                      <Box jfe>
                        <P s="S" c="text-secondary">
                          <TimeAgo date={txEvent.timestamp} />
                        </P>
                      </Box>
                    </Box>
                  </TableCell>
                  <BalanceContainer>
                    <Box dc afe>
                      <Box afe jfe fw>
                        <Strong uc s="L">
                          {showDecimalsIfNeeded(
                            Number(
                              getTransactionTokenAmount(txEvent, memecoin.decimals),
                            ),
                          )}
                        </Strong>
                        <P s="S">{memecoin?.tokenSymbol}</P>
                      </Box>
                      <P s="S" c="text-secondary">
                        {!!tokenPrice ? `$${(getTransactionPrice(txEvent, tokenPrice).toLocaleString('en-GB', {
                          maximumFractionDigits: 2,
                        }))}` : '--'}
                      </P>
                    </Box>
                  </BalanceContainer>
                </TableBodyRow>
              )
            })}
          </tbody>
        </Table>
      </Box>
      {pagination}
    </Box>
  )
}

export default PureTradesTable

const getTransactionPrice = (transaction, price = 0) => {
  const amount = getTransactionTokenAmount(transaction, 6)
  console.log({ test: Number(amount ?? 0) * price })
  return Number(amount ?? 0) * price
}


const Table = styled.table`
  width: 100%;
  table-layout: fixed;

  thead {
    font-weight: normal;
  }
`


const TableCell = styled.td`
  padding: var(--units-12, 12px) 0;
`

const BalanceContainer = styled(TableCell)`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  
  ${P} {
    margin-left: 4px;
  }
`;

const TableRow = styled.tr`
  width: 100%;
  display: table-row;
  justify-content: space-between;
  align-items: center;
  gap: var(--units-8, 8px);
  flex: 1 0 0;

  
  th:not(:first-child) {
    align-self: flex-end;
    text-align: right;
  }
`

const TableBodyRow = styled(TableRow)`
  padding: var(--units-16, 16px) 0;
  gap: 12px var(--units-12, 12px);
  border-bottom: 1px solid var(--stroke-tertiary, #252525);
  &:last-child {
    border-bottom: none;
  }
  cursor: pointer;

  :hover {
    border-radius: var(--units-4, 4px);
    background: var(--states-minimal-alpha-8, rgba(255, 255, 255, 0.08));
  }
`

const Pill = styled.div`
  display: flex;
  height: 30px;
  padding: 4px 6px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 6px;

  color: #222;

  /* Content/XS */
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

export const BuyPill = styled(Pill)`
  background: #d4f8d3;
  color: black;
`

export const SellPill = styled(Pill)`
  background: #fbe7e9;
  color: black;
`

const ExternalLink = styled.a`
  padding: 2px 6px;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 32px;
`
